import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';

// Map a Google event to the user's RSVP status — what the v2 pill shape
// communicates. Event-level `status` is just confirmed/cancelled; the
// per-attendee responseStatus is what actually answers "did I accept?".
function userResponseStatus(ev, calendarId) {
  if (ev.status === 'cancelled') return 'declined';
  const attendees = ev.attendees || [];
  // Prefer the attendee Google flag `self`; fall back to email match
  // against the calendar id (typically the account's email).
  const me = attendees.find(a => a.self) ||
             attendees.find(a => a.email && a.email.toLowerCase() === String(calendarId).toLowerCase());
  if (!me) return 'accepted'; // events you own with no attendees
  switch (me.responseStatus) {
    case 'declined':    return 'declined';
    case 'tentative':   return 'tentative';
    case 'accepted':    return 'accepted';
    case 'needsAction': return 'tentative';
    default:            return 'accepted';
  }
}

// Derive everything the schedule view needs from raw per-day fetch results:
//   - eventsByDayAndCal:   per-day, per-calendar pill data with sequential
//                          numbering shared with the daily summary
//   - visibleISOs:         ISO date strings for the days currently in view
//   - defaultSummaryISO:   today-if-visible, else the first day in view
//   - buildAllEventsForSummary(iso): per-day deduped + sorted summary feed
export function useEventsForView({ days, dayEventData, today, HOME_TZ }) {
  const eventsByDayAndCal = useMemo(() => {
    const map = {};
    days.forEach(d => {
      const iso = d.toISODate();
      const dayStart = d.setZone(HOME_TZ).startOf('day');
      const raw = dayEventData[iso]?.eventsByCalendar || {};
      const dayMap = {};
      // collect, sort chronologically, then assign sequentialNumber.
      const collected = [];
      Object.entries(raw).forEach(([calId, calData]) => {
        (calData?.events || [])
          .filter(ev => !ev.allDay && ev.startTime && ev.endTime)
          .forEach(ev => {
            const start = DateTime.fromISO(ev.startTime).setZone(HOME_TZ);
            const end = DateTime.fromISO(ev.endTime).setZone(HOME_TZ);
            const startHour = start.diff(dayStart, 'hours').hours;
            const durationHours = Math.max(0.25, end.diff(start, 'hours').hours);
            if (startHour < 0 || startHour >= 24) return;
            collected.push({
              calId,
              id: ev.eventId || `${calId}-${ev.startTime}`,
              title: ev.title || '(no title)',
              startHour,
              durationHours,
              status: userResponseStatus(ev, calId),
              startMs: start.toMillis(),
            });
          });
      });
      collected.sort((a, b) => a.startMs - b.startMs);
      collected.forEach((ev, i) => {
        const out = { ...ev, sequentialNumber: i + 1 };
        if (!dayMap[ev.calId]) dayMap[ev.calId] = [];
        dayMap[ev.calId].push(out);
      });
      map[iso] = dayMap;
    });
    return map;
  }, [days, dayEventData, HOME_TZ]);

  // All-day events per visible day, deduped, with calendar attribution
  // so the day-card top strip can render a per-event pill tinted by the
  // owning calendar's color. Spans (multi-day all-day) include every day
  // they cover.
  const allDayByDay = useMemo(() => {
    const map = {};
    days.forEach(d => {
      const iso = d.toISODate();
      const raw = dayEventData[iso]?.eventsByCalendar || {};
      const list = [];
      const seen = new Set();
      Object.entries(raw).forEach(([calId, cal]) => {
        (cal.events || []).forEach(ev => {
          if (!ev.allDay) return;
          // Multi-day all-day events: Google's `endTime` is exclusive.
          // Include this iso iff [startDate, endDate) covers it.
          const startDate = (ev.startTime || '').split('T')[0];
          const endDate = (ev.endTime || '').split('T')[0];
          if (!startDate) return;
          if (endDate && !(iso >= startDate && iso < endDate)) {
            // single-day allDay events sometimes have endDate === startDate
            if (!(iso === startDate && (!endDate || endDate === startDate))) return;
          }
          const key = ev.eventId || ev.id || `${calId}-${startDate}-${ev.title}`;
          if (seen.has(key)) return;
          seen.add(key);
          list.push({
            id: key,
            calId,
            title: ev.title || '(no title)',
            calendarName: cal.calendarName,
            calendarColor: cal.color,
            status: userResponseStatus(ev, calId),
          });
        });
      });
      map[iso] = list;
    });
    return map;
  }, [days, dayEventData]);

  const visibleISOs = useMemo(() => days.map(d => d.toISODate()), [days]);

  const defaultSummaryISO = useMemo(() => {
    const todayISO = today.toISODate();
    if (visibleISOs.includes(todayISO)) return todayISO;
    return visibleISOs[0];
  }, [visibleISOs, today]);

  // Per-day daily-summary feed builder — dedupe by eventId, attach
  // calendarName + calendarColor, filter to that date, sort, number.
  const buildAllEventsForSummary = useCallback((iso) => {
    const raw = dayEventData[iso]?.eventsByCalendar || {};
    if (!raw || Object.keys(raw).length === 0) return [];

    const combined = [];
    const seen = new Set();
    Object.values(raw).forEach(cal => {
      (cal.events || []).forEach(ev => {
        const key = ev.eventId || ev.id;
        if (seen.has(key)) return;
        seen.add(key);
        combined.push({ ...ev, calendarName: cal.calendarName, calendarColor: cal.color });
      });
    });

    const onSelected = combined.filter(ev => {
      if (ev.allDay) {
        const start = ev.startTime.split('T')[0];
        const end = ev.endTime.split('T')[0];
        return iso >= start && iso < end;
      }
      const evDate = DateTime.fromISO(ev.startTime).setZone(HOME_TZ).toISODate();
      return evDate === iso;
    });

    onSelected.sort((a, b) => {
      if (a.allDay && !b.allDay) return -1;
      if (!a.allDay && b.allDay) return 1;
      return DateTime.fromISO(a.startTime).toMillis() - DateTime.fromISO(b.startTime).toMillis();
    });

    let n = 1;
    return onSelected.map(ev => ({
      ...ev,
      sequentialNumber: ev.allDay ? null : n++,
    }));
  }, [dayEventData, HOME_TZ]);

  return {
    eventsByDayAndCal,
    allDayByDay,
    visibleISOs,
    defaultSummaryISO,
    buildAllEventsForSummary,
  };
}
